import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaLayerGroup, FaPlus, FaPen, FaArchive, FaStar, FaRegStar } from "react-icons/fa";
import { SUBPROJECTS_CONSTANTS } from "../_MODULES/_API";
import { SubProjectFormModal } from "./SubProjectFormModal";
import { STATUS_MAP_SUBPROJECT } from "../../../_Catalogs.Views";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { StatusBadge } from "../../../../../_CORE/APP/Templates/_UI/Status/_index";
import { Card, EmptyState, Chip } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { onSilentPatch } from "../../../../_UI/onSilentPatch";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { useDateRange, onMatchesRange } from "../../../../_UI/useDateRange";
import { onApiAction } from "../../../../_UI/onApiAction";
import { ViewPagination } from "../../../../_UI/ViewPagination";
import { usePagedList } from "../../../../_UI/usePagedList";

export const PanelSubProjects = ({ project, onChanged }) => {

    const [useLocalItems, setLocalItems] = useState(project.subProjects ?? []);

    const [useSearch, setSearch] = useState("");

    const DATE_RANGE = useDateRange();
    const [useSubProjectEdit, setSubProjectEdit] = useState(null);
    const [useIsModalOpen, setIsModalOpen] = useState(false);

    const { actionMutate } = useApiActionMutation({ isToastError: true, isToastSuccess: true, minTimeMutate: 300 });

    useEffect(() => { setLocalItems(project.subProjects ?? []); }, [project]);

    const SUB_PROJECTS = [...useLocalItems].sort((a, b) => (Number(!!b.isFavorite) - Number(!!a.isFavorite)) || (Number(a.position ?? 0) - Number(b.position ?? 0)) || String(a.name ?? "").localeCompare(String(b.name ?? "")));

    const PAGED_SOURCE = SUB_PROJECTS.filter((subProject) => onMatchesRange(subProject, DATE_RANGE.useRange, "createdAt"))
        .filter((subProject) => onMatchesSearch(useSearch, [
        subProject.name, subProject.type, subProject.description, subProject.repositoryUrl, ...(subProject.stack ?? []),
    ]));

    const VISIBLE = PAGED_SOURCE;

    const PAGER = usePagedList(VISIBLE, 6, "subprojectsPage");

    const onOpenCreate = () => {
        setSubProjectEdit(null);
        setIsModalOpen(true);
    };

    const onOpenEdit = (subProject) => {
        setSubProjectEdit(subProject);
        setIsModalOpen(true);
    };


    const onToggleFavorite = async (subProject) => {
        const previous = !!subProject.isFavorite;
        const next = !previous;
        setLocalItems((prev) => prev.map((item) => item.id === subProject.id ? { ...item, isFavorite: next } : item));

        const RESPONSE = await onSilentPatch({
            endpoint: SUBPROJECTS_CONSTANTS.UPDATE,
            id: subProject.id,
            data: { isFavorite: next },
            errorMessage: "No se pudo guardar el destacado del subproyecto.",
        });

        if (!RESPONSE.success) {
            setLocalItems((prev) => prev.map((item) => item.id === subProject.id ? { ...item, isFavorite: previous } : item));
        }
        // No onChanged(): destacar no vuelve a pedir el proyecto ni refresca la lista.
    };

    const onDelete = async (subProject) => {

        const IS_CONFIRMED = await confirm.danger(
            "Archivar subproyecto",
            `Se archiva "${subProject.name}" con su bitácora, tareas, entornos, scripts y archivos. Lo podés recuperar desde Archivados.`,
        );

        if (!IS_CONFIRMED) return;

        const RESPONSE = await onApiAction({
            method: "PATCH", endpoint: SUBPROJECTS_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: subProject.id }, successMessage: "Subproyecto archivado",
        });

        if (RESPONSE.success) setLocalItems((prev) => prev.filter((item) => item.id !== subProject.id));
    };

    return (
        <>
            <div className="LStudio__app_panel_bar">

                <ViewToolbar
                    filters={
                        <SearchFilter value={useSearch} onChange={setSearch} total={SUB_PROJECTS.length} matches={VISIBLE.length}
                            placeholder="Ej.: Frontend, React o API" />
                    }
                    dateRange={DATE_RANGE}
                    total={PAGER.total}
                    perPage={PAGER.usePerPage}
                    onChangePerPage={PAGER.onChangePerPage}
                    exportColumns={[
                        { key: "name", label: "Subproyecto" },
                        { key: "type", label: "Tipo" },
                        { key: "description", label: "Descripción" },
                        { key: "stack", label: "Stack" },
                        { key: "environments", label: "Entornos" },
                    ]}
                    exportRows={VISIBLE.map((subProject) => ({
                        name: subProject.name,
                        type: subProject.type,
                        description: subProject.description ?? "",
                        stack: (subProject.stack ?? []).join(", "),
                        environments: subProject.environments?.length ?? 0,
                    }))}
                    exportName="subproyectos"
                    actions={<AppButton icon={<FaPlus />} onClick={onOpenCreate}>Nuevo subproyecto</AppButton>} />

            </div>

            {
                VISIBLE.length === 0 ? (
                    <EmptyState icon={<FaLayerGroup />} title="Este proyecto todavía no tiene subproyectos"
                        description="Creá uno de frontend y otro de backend: cada uno lleva su propia bitácora, tareas, entornos, scripts y archivos."
                        action={<AppButton icon={<FaPlus />} onClick={onOpenCreate}>Nuevo subproyecto</AppButton>} />
                ) : (
                    <div className="LStudio__app_subprojects_grid">
                        {
                            PAGER.paged.map((subProject) => (
                                <Card key={subProject.id} className="LStudio__app_subprojects_card">

                                    <div className="LStudio__app_subprojects_card_head">

                                        <span className="LStudio__app_subprojects_card_dot"
                                            style={{ background: subProject.color || "var(--LS__accent, #e83a52)" }} />

                                        <Link to={`/projects/${project.id}/subprojects/${subProject.id}/logbook`}
                                            className="LStudio__app_subprojects_card_name">
                                            {subProject.isFavorite && <FaStar className="LStudio__app_record_star" title="Destacado" />}
                                            {subProject.name}
                                        </Link>

                                        <StatusBadge status={subProject.type} map={STATUS_MAP_SUBPROJECT} size="sm" />

                                        <CardActionMenu title="Acciones del subproyecto" items={[
                                            { key: "favorite", icon: subProject.isFavorite ? <FaStar /> : <FaRegStar />,
                                                label: subProject.isFavorite ? "Quitar de destacados" : "Destacar", onClick: () => onToggleFavorite(subProject) },
                                            { key: "edit", icon: <FaPen />, label: "Editar", onClick: () => onOpenEdit(subProject) },
                                            { divider: true },
                                            { key: "archive", icon: <FaArchive />, label: "Archivar", tone: "danger", onClick: () => onDelete(subProject) },
                                        ]} />

                                    </div>

                                    {subProject.description && <p className="LStudio__app_subprojects_card_desc">{subProject.description}</p>}

                                    {
                                        subProject.stack?.length > 0 && (
                                            <div className="LStudio__app_subprojects_card_stack">
                                                {subProject.stack.map((tech) => <Chip key={tech} tone="neutral">{tech}</Chip>)}
                                            </div>
                                        )
                                    }

                                    <p className="LStudio__app_subprojects_card_meta">
                                        {subProject.environments?.length ?? 0} entornos
                                    </p>

                                    <div className="LStudio__app_subprojects_card_actions">
                                        <AppButton size="sm" variant="soft"
                                            to={`/projects/${project.id}/subprojects/${subProject.id}/logbook`}>Abrir</AppButton>
                                    </div>

                                </Card>
                            ))
                        }
                    </div>
                )
            }

            <ViewPagination page={PAGER.usePage} totalPages={PAGER.totalPages}
                total={PAGER.total} perPage={PAGER.usePerPage} onChange={PAGER.onChangePage} />

            <SubProjectFormModal
                isOpen={useIsModalOpen}
                projectId={project.id}
                subProject={useSubProjectEdit}
                subProjects={useLocalItems}
                onClose={() => setIsModalOpen(false)}
                onSaved={(saved, isEdit) => setLocalItems((prev) => (isEdit
                    ? prev.map((item) => (item.id === saved.id ? { ...item, ...saved } : item))
                    : [...prev, saved]))} />
        </>
    );
};
